import AccountFlowLayout from "@/shared/components/AccountFlowLayout";
import {
  Box,
  Loader,
  Stack,
  Text,
  UnstyledButton,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { IconMailFast } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  getUserConfirmationStatus,
  resendConfirmationEmail,
} from "@/core/api/userApi";
import { notifications } from "@mantine/notifications";
import log from "loglevel";
import { RoutePaths } from "@/core/constants/RoutePaths";
import { useEmailConfirmationData } from "@/features/login/hooks/useEmailConfirmationData";

const MOBILE_USER_AGENT_REGEX =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

const WEBMAIL_BY_DOMAIN: Record<string, string> = {
  "gmail.com": "https://mail.google.com",
  "googlemail.com": "https://mail.google.com",
  "outlook.com": "https://outlook.live.com",
  "hotmail.com": "https://outlook.live.com",
  "live.com": "https://outlook.live.com",
  "yahoo.com": "https://mail.yahoo.com",
};

const EMAIL_NOT_CONFIRMED_MESSAGE =
  "Você ainda não confirmou seu e-mail. Clique no link que enviamos na sua caixa de entrada.";

function wait(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isMobileEnvironment() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const hasMatchMedia = typeof window.matchMedia === "function";
  const isMobileByMediaQuery = hasMatchMedia
    ? window.matchMedia("(max-width: 768px)").matches
    : false;
  const isMobileByUserAgent = MOBILE_USER_AGENT_REGEX.test(navigator.userAgent);

  return isMobileByMediaQuery || isMobileByUserAgent;
}

function resolveEmailDestination(
  email: string,
  isMobile: boolean
): string | null {
  if (isMobile) {
    return `mailto:${email}`;
  }

  const domain = email.split("@")[1]?.toLowerCase() ?? "";
  const webmailUrl = WEBMAIL_BY_DOMAIN[domain];

  if (webmailUrl) {
    return webmailUrl;
  }

  // No desktop, mailto: abre o compose do cliente padrão (não o inbox).
  // Para domínios desconhecidos, é melhor não exibir o botão do que confundir.
  return null;
}

export default function EmailConfirmationPage() {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [checkState, setCheckState] = useState<"idle" | "loading" | "error">(
    "idle"
  );
  const [isResending, setIsResending] = useState(false);

  const { colorScheme } = useMantineColorScheme();

  const isDark = colorScheme === "dark";
  const isMobile = isMobileEnvironment();

  const { email, id } = useEmailConfirmationData();

  const displayEmail = email ?? "seu e-mail";

  const bg = isDark ? theme.colors["D700"][0] : "#ffffff";
  const textMain = isDark ? theme.colors["L100"][0] : theme.colors["D900"][0];
  const textMuted = isDark ? theme.colors["L750"][0] : theme.colors["L750"][0];
  const red = theme.colors["R500"][0];
  const borderColor = isDark
    ? theme.colors["D400"][0]
    : theme.colors["L500"][0];
  const stepsBg = isDark ? theme.colors["D600"][0] : theme.colors["L200"][0];
  const width = isMobile ? rem(320) : rem(440);

  const openEmail = () => {
    if (!email) {
      notifications.show({
        title: "Não foi possível abrir seu e-mail",
        message:
          "Endereço de e-mail não encontrado. Tente realizar o cadastro novamente.",
        color: "red",
      });
      return;
    }

    const destination = resolveEmailDestination(email, isMobile);
    if (!destination) return;

    if (destination.startsWith("mailto:")) {
      window.open(destination, "_self");
      return;
    }

    window.open(destination, "_blank", "noopener,noreferrer");
  };

  const emailDestination = email
    ? resolveEmailDestination(email, isMobile)
    : null;

  const handleCheckEmailConfirmation = async () => {
    if (checkState === "loading") {
      return;
    }

    if (!id) {
      notifications.show({
        title: "Não foi possível verificar seu cadastro",
        message:
          "ID do usuário não encontrado. Tente realizar o cadastro novamente.",
        color: "red",
      });
      return;
    }

    try {
      setCheckState("loading");

      await wait(1800);

      const response = await getUserConfirmationStatus(id);

      if (response.data?.confirmed) {
        setCheckState("idle");
        notifications.show({
          title: "E-mail confirmado!",
          message: "Agora você já pode fazer login na sua conta.",
          color: "teal",
          autoClose: 3000,
        });

        navigate(RoutePaths.LOGIN, { replace: true });
      } else {
        setCheckState("error");
      }
    } catch (error) {
      log.error(error);
      setCheckState("error");
      notifications.show({
        title: "Erro ao verificar e-mail",
        message:
          "Ocorreu um erro ao verificar seu e-mail. Tente novamente em instantes.",
        color: "red",
      });
    }
  };

  const handleResendConfirmationEmail = async () => {
    if (isResending) {
      return;
    }

    if (!email) {
      notifications.show({
        title: "Não foi possível reenviar o e-mail",
        message:
          "Endereço de e-mail não encontrado. Tente realizar o cadastro novamente.",
        color: "red",
      });
      return;
    }

    try {
      setIsResending(true);
      await resendConfirmationEmail(email);

      notifications.show({
        title: "E-mail de confirmação enviado",
        message: "Verifique sua caixa de entrada e o spam em alguns instantes.",
        color: "teal",
        autoClose: 4000,
      });
    } catch (error) {
      log.error(error);
      notifications.show({
        title: "Erro ao reenviar e-mail",
        message:
          "Ocorreu um erro ao reenviar o e-mail de confirmação. Tente novamente em instantes.",
        color: "red",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AccountFlowLayout showLogo={false}>
      <Box
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingTop: rem(24),
          paddingBottom: rem(24),
        }}>
        <Box
          style={{
            width,
            background: bg,
            borderRadius: rem(20),
            padding: isMobile
              ? `${rem(32)} ${rem(24)}`
              : `${rem(48)} ${rem(40)}`,
            border: `1px solid ${borderColor}`,
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.4)"
              : "0 8px 32px rgba(0,0,0,0.07)",
          }}>
          <Stack
            gap={0}
            align="center">
            <Text
              style={{
                fontFamily: "Outfit, sans-serif",
                fontWeight: 800,
                fontSize: rem(28),
                lineHeight: 1.1,
                textAlign: "center",
                marginBottom: rem(32),
              }}>
              <span style={{ color: red }}>Super</span>
              <br />
              <span style={{ color: textMain }}>Agents</span>
            </Text>

            <Box
              style={{
                width: rem(64),
                height: rem(64),
                borderRadius: rem(16),
                background: `${red}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: rem(24),
              }}>
              <IconMailFast
                size={32}
                color={red}
              />
            </Box>

            <Text
              style={{
                fontFamily: "Outfit, sans-serif",
                fontWeight: 700,
                fontSize: rem(isMobile ? 18 : 20),
                color: textMain,
                textAlign: "center",
                lineHeight: 1.3,
                marginBottom: rem(8),
              }}>
              Confirme <span style={{ color: red }}>seu e-mail</span> para
              continuar
            </Text>

            <Text
              size="sm"
              style={{
                color: textMuted,
                textAlign: "center",
                lineHeight: 1.6,
                marginBottom: rem(24),
              }}>
              Enviamos um e-mail de confirmação para{" "}
              <strong style={{ color: textMain }}>{displayEmail}</strong>
            </Text>

            <Box
              style={{
                width: "100%",
                background: stepsBg,
                border: `1px solid ${borderColor}`,
                borderRadius: rem(12),
                padding: `${rem(16)} ${rem(20)}`,
                marginBottom: rem(24),
              }}>
              <Stack gap={rem(12)}>
                {[
                  "Acesse sua caixa de entrada",
                  "Clique no link de confirmação que enviamos",
                ].map((label, index) => (
                  <Box
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: rem(12),
                    }}>
                    <Box
                      style={{
                        width: rem(26),
                        height: rem(26),
                        borderRadius: "50%",
                        background: `${red}20`,
                        border: `1.5px solid ${red}55`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}>
                      <Text
                        style={{
                          fontSize: rem(12),
                          fontWeight: 700,
                          color: red,
                          lineHeight: 1,
                        }}>
                        {index + 1}
                      </Text>
                    </Box>
                    <Text
                      size="sm"
                      fw={500}
                      style={{ color: textMain }}>
                      {label}
                    </Text>
                  </Box>
                ))}
              </Stack>
            </Box>

            {emailDestination !== null && (
              <UnstyledButton
                onClick={openEmail}
                style={{
                  width: "100%",
                  background: red,
                  borderRadius: rem(10),
                  padding: `${rem(14)} ${rem(20)}`,
                  textAlign: "center",
                  cursor: "pointer",
                  marginBottom: rem(12),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: rem(8),
                }}>
                <IconMailFast
                  size={16}
                  color="#fff"
                />
                <Text
                  size="sm"
                  fw={700}
                  style={{ color: "#fff" }}>
                  Abrir meu e-mail
                </Text>
              </UnstyledButton>
            )}

            <Box style={{ width: "100%", marginBottom: rem(20) }}>
              <UnstyledButton
                onClick={handleCheckEmailConfirmation}
                disabled={checkState === "loading"}
                style={{
                  width: "100%",
                  border: `1.5px solid ${checkState === "error" ? "#ef444455" : borderColor}`,
                  borderRadius: rem(10),
                  padding: `${rem(12)} ${rem(20)}`,
                  textAlign: "center",
                  cursor: checkState === "loading" ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: rem(8),
                  opacity: checkState === "loading" ? 0.7 : 1,
                  transition: "border-color 0.2s ease, opacity 0.2s ease",
                }}>
                {checkState === "loading" ? (
                  <>
                    <Loader
                      size={14}
                      color={textMuted}
                    />
                    <Text
                      size="sm"
                      fw={600}
                      style={{ color: textMuted }}>
                      Verificando confirmação...
                    </Text>
                  </>
                ) : (
                  <Text
                    size="sm"
                    fw={600}
                    style={{ color: textMuted }}>
                    Já cliquei no link →
                  </Text>
                )}
              </UnstyledButton>

              {checkState === "error" && (
                <Box
                  style={{
                    marginTop: rem(10),
                    padding: `${rem(10)} ${rem(14)}`,
                    borderRadius: rem(8),
                    background: "#ef444415",
                    border: "1px solid #ef444430",
                  }}>
                  <Text
                    size="xs"
                    style={{
                      color: "#ef4444",
                      lineHeight: 1.5,
                      textAlign: "center",
                    }}>
                    {EMAIL_NOT_CONFIRMED_MESSAGE}
                  </Text>
                </Box>
              )}
            </Box>

            <Text
              size="sm"
              style={{ color: textMuted }}>
              Não recebeu o e-mail?{" "}
              <Text
                component="span"
                onClick={handleResendConfirmationEmail}
                style={{
                  color: red,
                  fontWeight: 600,
                  cursor: isResending ? "default" : "pointer",
                  opacity: isResending ? 0.7 : 1,
                }}>
                {isResending ? "Reenviando..." : "Reenviar"}
              </Text>
            </Text>
          </Stack>
        </Box>
      </Box>
    </AccountFlowLayout>
  );
}
